import dayjs from "dayjs";
import { AttendanceException } from "../services/model/AttendanceException";
import { getShiftLabel } from "./attendanceUtils";

const parseTimeOfDay = (
  value: string
): { hour: number; minute: number } | null => {
  if (!value) return null;
  // Accepts "HH:mm" or "HH:mm:ss"
  const parts = value.split(":");
  if (parts.length < 2) return null;
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  if (h < 0 || h > 23 || m < 0 || m > 59) return null;
  return { hour: h, minute: m };
};

const formatClockOfDay = (raw: string): string => {
  if (!raw) return "";
  const parsed = parseTimeOfDay(raw);
  if (!parsed) return raw;
  const { hour, minute } = parsed;
  const ap = hour < 12 ? "am" : "pm";
  let h12 = hour % 12;
  if (h12 === 0) h12 = 12;
  const mm = minute === 0 ? "00" : minute.toString().padStart(2, "0");
  return `${h12}:${mm}${ap}`;
};

export const getShiftTimeRange = (
  shiftStart: string,
  shiftEnd: string
): string => `${formatClockOfDay(shiftStart)} - ${formatClockOfDay(shiftEnd)}`;

/**
 * Formats an exception's `timestamp`/`confirmed time` for display. The backend
 * sends a full ISO datetime (e.g. "2026-06-15T11:48:01.196736") for some
 * exception types — render those as a clock time ("11:48am"). Plain "HH:mm[:ss]"
 * values are passed through `formatClockOfDay`. Falsy input yields "".
 */
export const formatExceptionTime = (raw: string | null | undefined): string => {
  if (!raw) return "";
  if (raw.includes("T") || raw.includes(" ")) {
    const parsed = dayjs(raw);
    return parsed.isValid() ? parsed.format("h:mma") : raw;
  }
  return formatClockOfDay(raw);
};

export const getExceptionShiftLabel = (
  exception: Pick<AttendanceException, "shift_start" | "shift_end">
): string => getShiftLabel(exception.shift_start, exception.shift_end);

export const getExceptionDetailsSummary = (
  exception: Pick<AttendanceException, "exception_type" | "details">
): string => {
  const d = exception.details ?? {};
  if (exception.exception_type === "no_data") {
    const hours = d.hours_lost;
    return hours != null ? `${hours} hours lost` : "No clock event recorded.";
  }
  if (exception.exception_type === "no_show") {
    const hours = d.hours_lost;
    return hours != null ? `${hours} hours lost` : "No clock event recorded.";
  }
  if (exception.exception_type === "supervisor_clock") {
    const count = d.employee_count;
    if (typeof count === "number") {
      return `${count} employee${count === 1 ? "" : "s"} pending review.`;
    }
    return "Awaiting supervisor-clock review.";
  }
  if (exception.exception_type === "late_arrival") {
    const mins = d.late_minutes;
    return mins != null ? `${mins} minutes late` : "";
  }
  return "";
};
